import { ActivityViewModel } from "../ViewModels";
import { useActivityEnricher } from "./useActivityEnricher";
import {
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainHookProps,
    ActionResponse,
    useDetailLifeCycle,
} from "@/Shared";

import { useActivity } from "@/OneLiners";
import { ActivityModel } from "../Models";


/**
 * useActivityDetail hook
 *
 * Domain-specific detail hook for Activity entities.
 *
 * Orchestrates:
 * - domain data access and enrichment,
 * - permissions and access rights,
 * - UI feedback and notifications,
 * - navigation and layout handling.
 *
 * The UI layer interacts with this hook exclusively through
 * a controlled API exposing the entity VM and lifecycle state,
 * without direct knowledge of domain internals.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 */
export function useActivityDetail(props: DomainHookProps): DomainDetailHook<ActivityViewModel> {

    const { accessRight, permissionHook } = props;
    const { domainType } = DOMAIN_CONFIGS.Activity;

    /** Activity domain enums for view model population */
    const { ActivityEnums } = useAppEnum();

    /** Activity domain data and operations */
    const activityHook = useActivity();

    /**
     * Domain enrichment layer
     *
     * Computes additional fields and cross-domain data
     * for Activity entities before exposing to the UI.
     */
    const {
        enricherResponse,
        getStrictEnrichedActivity,
    } = useActivityEnricher();

    /**
     * Combined response representing overall readiness
     * for initializing the Activity detail view.
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            activityHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: activityHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Detail lifecycle hook
     *
     * Responsibilities:
     * - Initializes the ActivityViewModel when data is available.
     * - Handles loading, enrichment, and readiness state.
     * - Provides a consistent public API to the UI layer.
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: activityHook,
        buildVM: (currentEntity: ActivityModel, isDisplaying: boolean) => {
            return new ActivityViewModel({
                activity: getStrictEnrichedActivity(currentEntity),
                viewEnums: ActivityEnums,
                accessRight,
                isDisplaying
            })
        },

    });

    /**
     * Public API exposed to the UI layer.
     *
     * The UI layer never interacts directly with domain logic,
     * only via this hook which exposes the entity VM, lifecycle state,
     * and any other actions provided by the lifecycle hook.
     */
    return {
        ...lifeCycleHook
    }
}